import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import SvgClients from '@/assets/widgets/empty-state-clients.svg';

import { getSectionClassProp } from '../helpers/get-section-class-prop';

import { CompanyContactsWidget } from './contacts/company-contacts-widget';
import { FreeTextFieldsWidget } from './free-text-fields/free-text-fields-widget';
import { CompanyFundingWidget } from './funding/company-funding-widget';
import { ListedInProjectsWidget } from './listed-in-projects/listed-in-projects-widget';
import { OfficeLocationsWidget } from './office-locations/office-locations-widget';
import { CompanyProductsWidget } from './products/company-products-widget';
import { CompanyRatingWidget } from './rating/company-rating-widget';
import { CompanySection } from './sections-pane/sections-pane.state';
import { NewsFeedWidget } from './news-feed/news-feed-widget';
import { CompanyTagsWidget } from './tags/company-tags-widget';
import {
  COLUMN_1_WIDTH,
  COLUMN_WIDTH,
  WIDGET_CLIENTS_HEIGHT,
  WIDGET_CLIENTS_ITEMS_LIMIT,
  WIDGET_EDITOR_NOTE_RANKING_HEIGHT,
  WIDGET_INVESTORS_HEIGHT,
  WIDGET_INVESTORS_ITEMS_LIMIT,
} from './utils/constants';
import { AboutWidget } from './about-widget';
import { ClientsWidget } from './clients/clients-widget';
import { EditorNoteWidget } from './editor-note-widget';
import { InvestorsWidget } from './investors/investors-widget';
import { CompanyExternalLinksWidget } from './external-links/company-external-links-widget';
import { companyIdState } from './company.state';
import { useIsSpotlightCompanyView } from './company-fetch-context';
import { useAtomValue } from 'jotai';

export const CompanyOverviewWidgetsGrid = () => {
  const companyId = useAtomValue(companyIdState);
  const { t } = useTranslation('companies');
  const isSpotlightCompanyView = useIsSpotlightCompanyView();

  return (
    <Grid $isSpotlight={isSpotlightCompanyView}>
      <About {...getSectionClassProp(CompanySection.ABOUT)} />
      <ExternalLinks
        {...getSectionClassProp(CompanySection.EXTERNAL_LINKS)}
        companyId={companyId}
      />

      <Funding
        {...getSectionClassProp(CompanySection.FUNDING)}
        companyId={companyId}
      />
      <ListedInProjects
        {...getSectionClassProp(CompanySection.LISTED_IN)}
        companyId={companyId}
        editable={!isSpotlightCompanyView}
      />
      {!isSpotlightCompanyView && (
        <Rating {...getSectionClassProp(CompanySection.RANKING)} />
      )}
      <Product {...getSectionClassProp(CompanySection.PRODUCT)} />
      <NewsFeed {...getSectionClassProp(CompanySection.NEWS_FEED)} />
      <Contacts {...getSectionClassProp(CompanySection.CONTACTS)} />

      <Investors
        {...getSectionClassProp(CompanySection.INVESTORS)}
        height={WIDGET_INVESTORS_HEIGHT}
        optionsListLimit={WIDGET_INVESTORS_ITEMS_LIMIT}
        emptyStateOptions={{
          label: t`company.emptyState.investorsStatement`,
          linkText: t`company.emptyState.clickToAdd`,
        }}
        companyId={companyId}
      />

      <Clients
        {...getSectionClassProp(CompanySection.CLIENTS)}
        height={WIDGET_CLIENTS_HEIGHT}
        optionsListLimit={WIDGET_CLIENTS_ITEMS_LIMIT}
        emptyStateOptions={{
          padding: '36px 60px !important',
          gap: 32,
          label: t`company.emptyState.clientsStatement`,
          linkText: t`company.emptyState.clickToAdd`,
          image: SvgClients,
          direction: 'column',
          labelGrow: 1,
          labelJustify: 'space-around',
          labelAlign: 'center',
        }}
      />

      <EditorNote
        {...getSectionClassProp(CompanySection.EDITOR_NOTE)}
        companyId={companyId}
      />

      <Tags
        {...getSectionClassProp(CompanySection.TAGS)}
        companyId={companyId}
      />

      <OfficeLocations
        {...getSectionClassProp(CompanySection.OFFICE_LOCATIONS)}
      />
      <FreeTextFields {...getSectionClassProp(CompanySection.FREE_TEXT)} />
    </Grid>
  );
};

const Grid = styled('div')<{ $isSpotlight?: boolean }>`
  display: grid;
  gap: 30px 30px;
  margin: 0 30px;
  grid-template-columns:
    minmax(0, ${COLUMN_1_WIDTH}px) minmax(0, ${COLUMN_WIDTH}px)
    minmax(0, ${COLUMN_WIDTH}px);
  grid-template-areas:
    'about about external-links'
    'funding investors clients'
    ${({ $isSpotlight }) =>
      $isSpotlight
        ? `'editor-note editor-note editor-note'`
        : `'editor-note editor-note ranking'`}
    'listed-in listed-in tags'
    'products products products'
    'news-feed news-feed news-feed'
    'contacts contacts contacts'
    'office-locations office-locations office-locations'
    'free-text-fields free-text-fields free-text-fields';
`;

const About = styled(AboutWidget)`
  grid-area: about;
`;

const EditorNote = styled(EditorNoteWidget)`
  grid-area: editor-note;
  width: 100%;
`;

const Funding = styled(CompanyFundingWidget)`
  grid-area: funding;
`;

const Rating = styled(CompanyRatingWidget)`
  grid-area: ranking;
  height: ${WIDGET_EDITOR_NOTE_RANKING_HEIGHT}px;
  max-height: ${WIDGET_EDITOR_NOTE_RANKING_HEIGHT}px;
`;

const Product = styled(CompanyProductsWidget)`
  grid-area: products;
`;

const ListedInProjects = styled(ListedInProjectsWidget)`
  grid-area: listed-in;
`;

const NewsFeed = styled(NewsFeedWidget)`
  grid-area: news-feed;
`;

const Contacts = styled(CompanyContactsWidget)`
  grid-area: contacts;
`;

const Investors = styled(InvestorsWidget)`
  grid-area: investors;
`;

const Clients = styled(ClientsWidget)`
  grid-area: clients;
`;

const Tags = styled(CompanyTagsWidget)`
  grid-area: tags;
`;

const ExternalLinks = styled(CompanyExternalLinksWidget)`
  grid-area: external-links;
`;

const FreeTextFields = styled(FreeTextFieldsWidget)`
  grid-area: free-text-fields;
`;

const OfficeLocations = styled(OfficeLocationsWidget)`
  grid-area: office-locations;
`;
